import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  SlackOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  GetSupplierDataAPI,
  GetRawMaterialDataAPI,
  CreateRawMaterialPurchaseAPI,
  GetRawMaterialPurchaseAPI,
  DeleteRawMaterialPurchaseAPI,
  UpdateRawMaterialPurchaseAPI,
} from '../../AllAPI/AdminAPI'

const RawMaterialPurchase = () => {

  const selectedYear = Number(sessionStorage.getItem('year'))

  /* ================= LIST ================= */
  const [purchases, setPurchases] = useState([])
  const [filteredPurchases, setFilteredPurchases] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')

  /* ================= MASTER ================= */
  const [suppliers, setSuppliers] = useState([])
  const [materials, setMaterials] = useState([])

  /* ================= MODALS ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  /* ================= EDIT ================= */
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingPurchaseId, setEditingPurchaseId] = useState(null)

  /* ================= HEADER INPUT ================= */
  const [purchaseDate, setPurchaseDate] = useState(null)
  const [supplierId, setSupplierId] = useState(null)

  /* ================= ITEM INPUT ================= */
  const [materialId, setMaterialId] = useState(null)
  const [qty, setQty] = useState('')
  const [rate, setRate] = useState('')
  const [taxPercent, setTaxPercent] = useState('')

  /* ================= TEMP ITEMS ================= */
  const [items, setItems] = useState([])

  /* ================= VIEW / DELETE ================= */
  const [selectedPurchase, setSelectedPurchase] = useState(null)
  const [deletePurchaseId, setDeletePurchaseId] = useState(null)

  /* ================= SUMMARY ================= */
  const [discount, setDiscount] = useState('')
  const [remark, setRemark] = useState('')

  useEffect(() => {
    fetchSuppliers()
    fetchMaterials()
    fetchPurchases()
  }, [])

  /* ================= FETCH ================= */
  const fetchSuppliers = async () => {
    const res = await axiosInstance.get(`${GetSupplierDataAPI}/${selectedYear}`)
    setSuppliers(Array.isArray(res.data) ? res.data : [])
  }

  const fetchMaterials = async () => {
    const res = await axiosInstance.get(`${GetRawMaterialDataAPI}/${selectedYear}`)
    setMaterials(Array.isArray(res.data) ? res.data : [])
  }

  const fetchPurchases = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(`${GetRawMaterialPurchaseAPI}/${selectedYear}`)

      const mapped = (Array.isArray(res.data) ? res.data : []).map((p) => ({
        purchase_id: p.purchase_id,
        date: p.date,
        supplier_id: p.supplier_id,
        supplier_name: p.supplier_name,
        gross_total: p.gross_total,
        total_tax: p.total_tax_amount,
        discount: p.discount,
        net_amount: p.net_amount,
        remark: p.remark,
        items: p.items.map((i) => ({
          material_id: i.material_id,
          material_name: i.material_name,
          qty: i.quantity,
          rate: i.rate,
          tax_percent: i.tax_percentage,
          amount: i.amount,
        })),
      }))

      setPurchases(mapped)
      setFilteredPurchases(mapped)
    } finally {
      setLoading(false)
    }
  }

  /* ================= SEARCH ================= */
  const handleSearch = (value) => {
    setSearchText(value)
    setFilteredPurchases(
      purchases.filter((p) =>
        `${p.supplier_name} ${p.date}`
          .toLowerCase()
          .includes(value.toLowerCase()),
      ),
    )
  }

  /* ================= ADD ITEM ================= */
  const addItem = () => {
    if (!purchaseDate) return toast.error('Select purchase date')
    if (!supplierId) return toast.error('Select supplier')
    if (!materialId) return toast.error('Select material')
    if (!qty || qty <= 0) return toast.error('Enter valid qty')
    if (!rate || rate <= 0) return toast.error('Enter valid rate')

    if (items.find((i) => i.material_id === materialId))
      return toast.error('Material already added')

    const material = materials.find((m) => m.id === materialId)
    const amount = Number(qty) * Number(rate)

    setItems([
      ...items,
      {
        material_id: materialId,
        material_name: material.material_name,
        qty: Number(qty),
        rate: Number(rate),
        tax_percent: Number(taxPercent) || 0,
        amount,
      },
    ])

    setMaterialId(null)
    setQty('')
    setRate('')
    setTaxPercent('')
  }

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index))
  }

  /* ================= TOTALS ================= */
  const grossTotal = items.reduce((s, i) => s + i.amount, 0)
  const totalTax = items.reduce(
    (s, i) => s + (i.amount * i.tax_percent) / 100,
    0,
  )
  const netAmount = grossTotal + totalTax - (Number(discount) || 0)

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!items.length) return toast.error('Add at least one material')

    const payload = {
      date: purchaseDate.format('YYYY-MM-DD'),
      supplier_id: supplierId,
      gross_total: grossTotal,
      total_tax_amount: totalTax,
      discount: Number(discount) || 0,
      net_amount: netAmount,
      year: selectedYear,
      remark,
      items: items.map((i) => ({
        material_id: i.material_id,
        quantity: i.qty,
        rate: i.rate,
        tax_percentage: i.tax_percent,
        amount: i.amount,
      })),
    }

    const res = isEditMode
      ? await axiosInstance.put(
          `${UpdateRawMaterialPurchaseAPI}/${editingPurchaseId}`,
          payload,
        )
      : await axiosInstance.post(CreateRawMaterialPurchaseAPI, payload)

    if (res.data === 'SAVED' || res.data === 'UPDATED') {
      toast.success(isEditMode ? 'Purchase updated' : 'Purchase saved')
      setIsModalOpen(false)
      resetForm()
      fetchPurchases()
    }
  }

  const resetForm = () => {
    setPurchaseDate(null)
    setSupplierId(null)
    setItems([])
    setDiscount('')
    setRemark('')
    setIsEditMode(false)
    setEditingPurchaseId(null)
  }

  /* ================= EDIT ================= */
  const handleEdit = (record) => {
    setIsEditMode(true)
    setEditingPurchaseId(record.purchase_id)
    setPurchaseDate(dayjs(record.date))
    setSupplierId(record.supplier_id)
    setItems(record.items)
    setDiscount(record.discount)
    setRemark(record.remark)
    setIsModalOpen(true)
  }

  /* ================= DELETE ================= */
  const openDeleteModal = (record) => {
    setDeletePurchaseId(record.purchase_id)
    setDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    await axiosInstance.delete(
      `${DeleteRawMaterialPurchaseAPI}/${deletePurchaseId}`,
    )
    toast.success('Purchase deleted')
    setDeleteModal(false)
    fetchPurchases()
  }

  /* ================= TABLES ================= */
  const purchaseColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    { title: 'Supplier', dataIndex: 'supplier_name' },
    { title: 'Net Amount', dataIndex: 'net_amount' },
    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedPurchase(record)
              setViewModal(true)
            }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => openDeleteModal(record)}
          />
        </>
      ),
    },
  ]

  const tempColumns = [
    { title: 'Material', dataIndex: 'material_name' },
    { title: 'Qty', dataIndex: 'qty' },
    { title: 'Rate', dataIndex: 'rate' },
    { title: 'Tax %', dataIndex: 'tax_percent' },
    { title: 'Amount', dataIndex: 'amount' },
    {
      title: 'Action',
      render: (_, __, index) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeItem(index)}
        />
      ),
    },
  ]

  const viewColumns = tempColumns.filter((c) => c.title !== 'Action')

  return (
    <>
      <div className="mainContainer">
        <div className="header-row">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Raw Material
            Purchase
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            Add
          </Button>

          <Input
            placeholder="Search supplier / date"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={purchaseColumns}
          dataSource={filteredPurchases}
          rowKey="purchase_id"
          loading={loading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= ADD / EDIT MODAL ================= */}
      <Modal
        title="Raw Material Purchase"
        open={isModalOpen}
        onCancel={() => (setIsModalOpen(false), resetForm())}
        onOk={handleSubmit}
          okText="Save"
        width={900}
      >
        <div className="row">
          <div className="col-md-4 py-1">
            <DatePicker
              value={purchaseDate}
              onChange={setPurchaseDate}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-md-8 py-1">
            <Select
              value={supplierId}
              onChange={setSupplierId}
              placeholder="Select Supplier"
              style={{ width: '100%' }}
            >
              {suppliers.map((s) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.supplier_name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-md-4 py-1">
            <Select
              value={materialId}
              onChange={setMaterialId}
              placeholder="Material"
              style={{ width: '100%' }}
            >
              {materials.map((m) => (
                <Select.Option key={m.id} value={m.id}>
                  {m.material_name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="col-md-2 py-1">
            <Input
              type="number"
              placeholder="Qty"
              value={qty}
                step="1"
              onKeyDown={(e) => {
                if (e.key === '.' || e.key === ',') {
                  e.preventDefault()
                }
              }}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div className="col-md-2 py-1">
            <Input
              type="number"
              placeholder="Rate"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>

          <div className="col-md-2 py-1">
            <Input
              type="number"
              placeholder="Tax %"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
            />
          </div>

          <div className="col-md-2 py-1">
            <Button type="primary" onClick={addItem}>
              Add
            </Button>
          </div>
        </div>

        <Table
          className="mt-3 shadow"
          columns={tempColumns}
          dataSource={items}
          rowKey={(_, index) => index}
          pagination={false}
          style={{ overflowX: 'auto' }}
        />

        <div className="text-start mt-2">
          <div className="row mt-2">
            <div className="col-md-4 mt-1">
              <p>
                Gross Total : <b>₹ {grossTotal}</b>
              </p>
            </div>
            <div className="col-md-4 mt-1">
              <p>
                Total Tax : <b>₹ {totalTax} </b>
              </p>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-md-4 mt-1">
            <label>Discount (₹)</label>
            <Input
              placeholder="Discount"
              type="number"
              value={discount}
              step="1"
              onKeyDown={(e) => {
                if (e.key === '.' || e.key === ',') {
                  e.preventDefault()
                }
              }}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </div>
          <div className="col-md-8 mt-1">
            <label>Remark</label>
            <Input.TextArea
              rows={2}
              placeholder="Remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>

           <div className="col-md-4 mt-3">
              <span className='p-2' style={{backgroundColor:"yellow"}}>
                Net Amount : <b>₹ {netAmount}</b>
              </span>
            </div>
        </div>
      </Modal>

      {/* ================= VIEW MODAL ================= */}
      <Modal
        title="Purchase Details"
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={null}
        width={700}
      >
        <Table
          columns={viewColumns}
          dataSource={selectedPurchase?.items || []}
          rowKey={(_, index) => index}
          pagination={false}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />

        <div className="row mt-3">
          <div className="col-md-6 d-flex justify-content-between my-1">
            <span>Gross Total :</span>
            <b>₹ {selectedPurchase?.gross_total}</b>
          </div>

          <div className="col-md-6 d-flex justify-content-between my-1">
            <span>Total Tax :</span>
            <b>₹ {selectedPurchase?.total_tax}</b>
          </div>

          <div className="col-md-6 d-flex justify-content-between my-1">
            <span>Discount :</span>
            <b>₹ {selectedPurchase?.discount}</b>
          </div>

          <div className="col-md-6 d-flex justify-content-between my-1" style={{backgroundColor:"yellow"}}>
            <span>Net Amount :</span>
            <b>₹ {selectedPurchase?.net_amount}</b>
          </div>
        </div>
      </Modal>

      {/* ================= DELETE MODAL ================= */}
      <Modal
        title="Confirm Delete"
        open={deleteModal}
        onCancel={() => setDeleteModal(false)}
        onOk={handleDeleteConfirm}
        okType="danger"
      >
        Are you sure you want to delete this purchase?
      </Modal>
    </>
  )
}

export default RawMaterialPurchase
